import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ENV } from '../config/env';

// Types for API responses
export interface ApiResponse<T = unknown> {
  data: T | null;
  success: boolean;
  message?: string;
  errors?: string[];
}

type RetryableRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
  headers?: Record<string, string>;
};

type ErrorPayload = {
  message?: string;
  errors?: string[];
};

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  roles: string[];
  groups: string[];
  permissions: string[];
}

export interface RoleInfo {
  role: 'Admin' | 'NormalUser' | 'NotAssigned';
  permissions: string[];
  groups: string[];
}

export interface Building {
  id: number;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  floorCount?: number;
  squareFootage?: number;
  buildingType?: string;
  constructionDate?: string;
  status?: string;
  manager?: string;
  contactPhone?: string;
  contactEmail?: string;
}

class ApiService {
  private readonly axiosInstance: AxiosInstance;
  private getAccessToken: (() => Promise<string | null>) | null = null;

  constructor() {
    // Use empty base URL to leverage Vite dev proxy (endpoints start with /api)
    // or call the API directly when explicitly configured
    const baseURL = ENV.USE_DIRECT_API ? ENV.API_BASE_URL : '';

    this.axiosInstance = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  // Set the method to get access token
  setTokenProvider(getAccessToken: () => Promise<string | null>) {
    this.getAccessToken = getAccessToken;
  }

  private async handle401Retry(error: unknown): Promise<AxiosResponse | null> {
    if (!axios.isAxiosError(error)) {
      return null;
    }

    const originalRequest = (error.config || {}) as RetryableRequestConfig;

    if (error.response?.status !== 401 || originalRequest._retry) {
      return null;
    }

    originalRequest._retry = true;

    try {
      if (!this.getAccessToken) {
        return null;
      }

      const newToken = await this.getAccessToken();
      if (!newToken) {
        return null;
      }

      originalRequest.headers = originalRequest.headers ?? {};
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return this.axiosInstance(originalRequest);
    } catch (refreshError) {
      console.info('Token refresh failed:', refreshError);
      try {
        globalThis.location.href = '/login';
      } catch (redirectError) {
        // Silently ignore redirect errors in non-browser environments (e.g., SSR, tests)
        console.info('Location redirect failed:', redirectError);
      }
      const err = refreshError instanceof Error ? refreshError : new Error(String(refreshError));
      throw err;
    }
  }

  private setupInterceptors() {
    // Request interceptor to add JWT token
    this.axiosInstance.interceptors.request.use(
      async config => {
        if (this.getAccessToken) {
          const token = await this.getAccessToken();
          if (token) {
            config.headers = config.headers || {};
            (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      error => {
        const err = error instanceof Error ? error : new Error(error?.message ?? String(error));
        throw err;
      }
    );

    // Response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => response,
      async error => {
        const retryResponse = await this.handle401Retry(error);
        if (retryResponse) {
          return retryResponse;
        }

        const err = error instanceof Error ? error : new Error(error?.message ?? String(error));
        throw err;
      }
    );
  }

  // Helper: fallback GET (used when local dev proxy fails)
  private async tryFallbackGet<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const token = this.getAccessToken ? await this.getAccessToken() : null;
      const absoluteUrl = new URL(url, ENV.API_BASE_URL).toString();

      // Start with an empty options object and copy relevant keys from config
      const fallbackOpts: AxiosRequestConfig = {};
      if (config) Object.assign(fallbackOpts, config);

      // Only set headers when there are header entries or we need to add Authorization
      const cfgHeaders = config?.headers && Object.keys(config.headers).length
        ? (config.headers as Record<string, string>)
        : undefined;

      if (token) {
        fallbackOpts.headers = cfgHeaders ? { ...cfgHeaders, Authorization: `Bearer ${token}` } : { Authorization: `Bearer ${token}` };
      } else if (cfgHeaders) {
        fallbackOpts.headers = cfgHeaders;
      }

      const fallback = await axios.get<T>(absoluteUrl, fallbackOpts);
      return { data: fallback.data, success: true };
    } catch (fallbackError) {
      return this.handleError(fallbackError);
    }
  }

  // Generic API methods
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.get<T>(url, config);
      return {
        data: response.data,
        success: true,
      };
    } catch (error: unknown) {
      // Fallback: if proxy/connection error and we are not in direct mode, retry directly against ENV.API_BASE_URL
      const axiosError = axios.isAxiosError(error) ? error : null;
      const code = axiosError?.code || '';
      const status = axiosError?.response?.status;
      const looksLikeProxyFailure = code === 'ECONNREFUSED' || code === 'ERR_NETWORK' || status === 502 || status === 503 || status === 504;
      const shouldFallback = !ENV.USE_DIRECT_API && looksLikeProxyFailure;
      if (shouldFallback) {
        return await this.tryFallbackGet<T>(url, config);
      }
      return this.handleError(error);
    }
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.post<T>(url, data, config);
      return {
        data: response.data,
        success: true,
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.put<T>(url, data, config);
      return {
        data: response.data,
        success: true,
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.axiosInstance.delete<T>(url, config);
      return {
        data: response.data,
        success: true,
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  // User and role-specific API methods
  async getUserProfile(): Promise<ApiResponse<UserProfile>> {
    return this.get<UserProfile>('/api/user/profile');
  }

  async getUserRoles(): Promise<ApiResponse<RoleInfo>> {
    return this.get<RoleInfo>('/api/user/roles');
  }

  async validateToken(): Promise<ApiResponse<{ valid: boolean; expires: string }>> {
    return this.get<{ valid: boolean; expires: string }>('/api/auth/validate');
  }

  async refreshToken(): Promise<ApiResponse<{ accessToken: string; expiresIn: number }>> {
    return this.post<{ accessToken: string; expiresIn: number }>('/api/auth/refresh');
  }
 
  async accessRequest(reason: string): Promise<ApiResponse<unknown>> {
    return this.post<unknown>('/api/users/access-requests', { description: reason });
  }

  // SmartAlarm specific API methods (based on existing pages)
  async getDashboardData(): Promise<ApiResponse<unknown>> {
    return this.get<unknown>('/api/dashboard');
  }

  async getBuildings(): Promise<ApiResponse<Building[]>> {
    return this.get<Building[]>('/api/buildings');
  }

  async getBuildingById(id: number): Promise<ApiResponse<Building>> {
    return this.get<Building>(`/api/buildings/${id}`);
  }

  async getUserInfo(): Promise<ApiResponse<unknown>> {
    return this.get<unknown>('/api/buildings/user-info');
  }

  async getDistributionGroups(): Promise<ApiResponse<unknown[]>> {
    return this.get<unknown[]>('/api/distribution-groups');
  }

  async getSchedules(): Promise<ApiResponse<unknown[]>> {
    return this.get<unknown[]>('/api/schedules');
  }

  async getPendingAlarms(): Promise<ApiResponse<unknown[]>> {
    return this.get<unknown[]>('/api/alarms/pending');
  }

  async getAnalyticsData(): Promise<ApiResponse<unknown>> {
    return this.get<unknown>('/api/analytics');
  }

  async getAccessRequestData(): Promise<ApiResponse<unknown>> {
    return this.get<unknown>('/api/users/access-requests');
  }

  async deleteAccessRequestData(id:string): Promise<ApiResponse<unknown>> {
    return this.delete<unknown>(`/api/users/delete-access-requests/${id}`);
  }

  async getAccessRequestDataCount(): Promise<ApiResponse<unknown>> {
    return this.get<unknown>('/api/users/access-requests/count');
  }

  // Error handling
  private handleError<T>(error: unknown): ApiResponse<T> {
    console.info('API Error:', error);

    if (axios.isAxiosError<ErrorPayload | string>(error) && error.response) {
      // Server responded with error status
      const status = error.response.status;
      const statusText = error.response.statusText || '';
      const serverData = error.response.data;
      const serverPayload = typeof serverData === 'string' ? undefined : serverData;
      const serverMsg = typeof serverData === 'string' && serverData.trim()
        ? serverData
        : (serverPayload?.message || error.message);
      const msg = 'HTTP ' + status + ' ' + statusText + (serverMsg ? ': ' + serverMsg : '');
      return {
        data: null,
        success: false,
        message: msg,
        errors: serverPayload?.errors || [serverMsg],
      };
    }

    if (axios.isAxiosError(error) && error.request) {
      // Network error
      return {
        data: null,
        success: false,
        message: 'Network error - please check your connection',
        errors: ['NETWORK_ERROR'],
      };
    }

    // Other error
    return {
      data: null,
      success: false,
      message: error instanceof Error ? error.message : 'An unexpected error occurred',
      errors: ['UNKNOWN_ERROR'],
    };
  }
}

// Create and export a singleton instance
export const apiService = new ApiService();

// Hook for easier usage in React components
export const useApiService = () => apiService;

export default apiService;